import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import moment from "moment";
import { AuthContext } from "../../../../../../auth-context";
import { PreviewSurveyPDF } from "./survey-pdf";
import Modal from "../../../../../../components/modal";
import {
  getResponseCheckinData,
  getPortalSurveyResponse,
} from "../../../../../../api";
import Icon from "@material-ui/core/Icon";
import { SurveyResponse } from "./survey-response";

const Date = styled.p`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 auto 4px auto;
  text-align: center;
`;

const DateContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const DateButton = styled.button`
  border: none;
  background: none;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  padding: 0;
  font-size: 1.75rem;
  opacity: ${(props) => (props.disabled ? 0 : 1)};
`;

const formatDate = (date) => {
  return moment.utc(date).format("MMM D, YYYY");
};

export const SurveyModal = ({
  open,
  onClose,
  data = {},
  clientUserID = null,
}) => {
  const { user } = useContext(AuthContext);
  const [responses, setResponses] = useState([]);
  const [selectedResponseIndex, setSelectedResponseIndex] = useState(0);
  const [openPDF, setOpenPDF] = useState(false);
  const [checkInData, setCheckInData] = useState({});

  useEffect(() => {
    if (open && clientUserID) {
      getPortalSurveyResponse(user.id, clientUserID)
        .then((res) => {
          setResponses(res?.data?.responses);
          setSelectedResponseIndex(res?.data?.responses.length - 1);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [open, user.id, clientUserID]);

  useEffect(() => {
    const currentData = clientUserID ? responses[selectedResponseIndex] : data;
    if (!currentData || !currentData.user_id || !currentData.response_data) {
      return;
    }

    const hasCheckInQuestions = currentData.response_data.some((response) => {
      const question = currentData.survey_data?.questions?.find(
        (q) => q.id === response.question_id
      );
      return question?.responseType === "select_check_in";
    });
    
    if (!hasCheckInQuestions) {
      return;
    }

    const fetchCheckInData = async () => {
      const checkInPromises = currentData.response_data
        .filter((response) => {
          const question = currentData.survey_data?.questions?.find(
            (q) => q.id === response.question_id
          );
          return question?.responseType === "select_check_in";
        })
        .map(async (response) => {
          try {
            const res = await getResponseCheckinData(
              user.id,
              currentData.user_id,
              response.response
            );
            return {
              questionId: response.question_id,
              checkIn: res.data.check_in,
            };
          } catch (error) {
            console.error("Error fetching check-in:", error);
            return {
              questionId: response.question_id,
              checkIn: null,
            };
          }
        });

      const results = await Promise.all(checkInPromises);
      const checkInMap = {};
      results.forEach((result) => {
        checkInMap[result.questionId] = result.checkIn;
      });
      setCheckInData(checkInMap);
    };
    
    fetchCheckInData();
  }, [clientUserID ? responses[selectedResponseIndex]?.id : data?.id, user.id]);

  const handlePrevious = () => {
    if (selectedResponseIndex > 0) {
      setSelectedResponseIndex(selectedResponseIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedResponseIndex < responses.length - 1) {
      setSelectedResponseIndex(selectedResponseIndex + 1);
    }
  };

  const showNextButton = selectedResponseIndex < responses.length - 1;
  const showPreviousButton = selectedResponseIndex > 0;
  const currentData = clientUserID ? responses[selectedResponseIndex] : data;

  return (
    <>
      <Modal
        open={open}
        onClose={() => onClose()}
        handleSubmit={() => setOpenPDF(true)}
        submitLabel="Export as PDF"
      >
        {clientUserID
          ? // Navigating responses (Overview, Check-in Forms page)
            responses.length > 0 && (
              <>
                <DateContainer>
                  <DateButton
                    onClick={showPreviousButton ? handlePrevious : undefined}
                    disabled={!showPreviousButton}
                  >
                    <Icon>chevron_left</Icon>
                  </DateButton>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Date>
                      Check-in Date: {formatDate(currentData?.report_date)}
                    </Date>
                    <Date>
                      Response Date: {formatDate(currentData?.created_at)}
                    </Date>
                  </div>

                  <DateButton
                    onClick={showNextButton ? handleNext : undefined}
                    disabled={!showNextButton}
                  >
                    <Icon>chevron_right</Icon>
                  </DateButton>
                </DateContainer>

                <SurveyResponse data={currentData} checkInData={checkInData} />
              </>
            )
          : // Single response (Timeline, Recent Check-ins)
            currentData && (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Date>
                    Check-in Date: {formatDate(currentData?.report_date)}
                  </Date>
                  <Date>
                    Response Date: {formatDate(currentData?.created_at)}
                  </Date>
                </div>
                <SurveyResponse data={currentData} checkInData={checkInData} />
              </>
            )}
      </Modal>

      <PreviewSurveyPDF
        open={openPDF}
        handleClose={() => setOpenPDF(false)}
        data={currentData}
        checkInData={checkInData}
      />
    </>
  );
};
